import { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { errorMessage } from "../../../api/client";
import { shiftApi } from "../../../api/resources";
import type { AttentionFlag, HistoryEntry, ShiftAdmin } from "../../../api/types";
import { Drawer, Modal } from "../../../components/Overlay";
import { Button, DemoBadge, ErrorText, Field, FullScreenLoader, TextInput } from "../../../components/ui";
import { fmtDate } from "../../../utils/labels";
import { ShiftHistory } from "./ShiftHistory";
import { ResponsePanel } from "./CrewResponse";
import { AttentionIcon, CrewBadges, FLAG_LABEL, ShiftStatusPill, fmtWorkDate, timeRange } from "./shiftUi";
import { SeriesPanel } from "./SeriesPanel";

function Row({ label, children, testId }: { label: string; children: React.ReactNode; testId: string }) {
  return (
    <div className="grid gap-1 border-b border-white/5 py-2.5 sm:grid-cols-[140px_1fr]">
      <dt className="label-caps text-zinc-500">{label}</dt>
      <dd data-testid={testId} className="break-words text-sm text-white">{children}</dd>
    </div>
  );
}

function FlagItem({ shiftId, flag, onDone }: { shiftId: string; flag: AttentionFlag; onDone: (s: ShiftAdmin) => void }) {
  const [open, setOpen] = useState(false);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);
  const resolved = !!flag.resolved_at;
  const submit = async () => {
    setBusy(true);
    try {
      onDone(await shiftApi.acknowledge(shiftId, flag.id, note.trim()));
      toast.success("Flag acknowledged");
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <li data-testid={`drawer-flag-${flag.id}`} className={`rounded-sm border p-3 ${resolved ? "border-white/10 opacity-60" : "border-signal/50 bg-signal/10"}`}>
      <p className="flex items-center gap-2 text-sm font-semibold text-white"><AttentionIcon show={!resolved} testId={`drawer-flag-icon-${flag.id}`} />{FLAG_LABEL[flag.code]}<span data-testid={`drawer-flag-code-${flag.id}`} className="ml-auto font-mono text-[10px] font-normal text-zinc-500">{flag.code}</span></p>
      <p className="mt-1 text-xs text-zinc-300">{flag.message}</p>
      {resolved ? (
        <p data-testid={`drawer-flag-resolution-${flag.id}`} className="mt-1 text-xs text-zinc-400">
          {flag.resolved_by === "SYSTEM" ? "Auto-resolved" : "Acknowledged"} {fmtDate(flag.resolved_at)}{flag.resolution_note ? ` · ${flag.resolution_note}` : ""}
        </p>
      ) : open ? (
        <div className="mt-3 flex gap-2">
          <TextInput value={note} onChange={(e) => setNote(e.target.value)} placeholder="Note (required)" className="h-9 text-sm" data-testid={`drawer-flag-note-${flag.id}`} />
          <Button className="h-9 px-3 text-[11px]" loading={busy} disabled={note.trim().length < 3} onClick={submit} data-testid={`drawer-flag-ack-submit-${flag.id}`}>Save</Button>
        </div>
      ) : (
        <Button variant="secondary" className="mt-3 h-8 px-3 text-[11px]" onClick={() => setOpen(true)} data-testid={`drawer-flag-ack-${flag.id}`}>Acknowledge</Button>
      )}
    </li>
  );
}

interface Props {
  id: string | null;
  reload: number;
  onClose: () => void;
  onChanged: () => void;
  onEdit: (s: ShiftAdmin) => void;
}

export function ShiftDrawer({ id, reload, onClose, onChanged, onEdit }: Props) {
  const [shift, setShift] = useState<ShiftAdmin | null>(null);
  const [history, setHistory] = useState<HistoryEntry[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [cancelling, setCancelling] = useState(false);

  const load = useCallback(() => {
    if (!id) return;
    setError(null);
    shiftApi.get(id).then(setShift).catch((e) => setError(errorMessage(e)));
    shiftApi.history(id).then(setHistory).catch(() => setHistory([]));
  }, [id]);
  useEffect(() => {
    setShift(null);
    load();
  }, [load, reload]);

  const changed = () => {
    load();
    onChanged();
  };
  const confirm = async () => {
    if (!shift) return;
    try {
      await shiftApi.confirm(shift.id);
      toast.success("Shift confirmed");
      changed();
    } catch (err) {
      toast.error(errorMessage(err));
    }
  };

  const s = shift;
  const live = s && s.status !== "CANCELLED" && s.status !== "COMPLETED";
  return (
    <Drawer open={!!id} onClose={onClose} title="Shift detail" testId="shift-drawer">
      <ErrorText message={error} testId="shift-drawer-error" />
      {!s && !error && <FullScreenLoader />}
      {s && (
        <div className="space-y-8">
          <div>
            <div className="flex flex-wrap items-center gap-2">
              <ShiftStatusPill status={s.status} testId="drawer-status" />
              <DemoBadge show={s.is_demo} testId="drawer-demo-badge" />
              <CrewBadges s={s} testPrefix="drawer" />
              {s.is_past && <span className="text-[10px] uppercase tracking-wider text-zinc-500">Past date</span>}
            </div>
            <p data-testid="drawer-date" className="mt-3 font-display text-3xl font-bold uppercase tracking-tight text-white">{fmtWorkDate(s.work_date)}</p>
            <p data-testid="drawer-time" className="mt-1 text-lg text-zinc-200">{timeRange(s)}</p>
            <p data-testid="drawer-timezone" className="text-xs text-zinc-500">Venue local time · {s.venue_timezone} ({s.tz_abbr})</p>
          </div>
          {live && (
            <div className="flex flex-wrap gap-2">
              <Button variant="secondary" className="h-9 px-4 text-[11px]" onClick={() => onEdit(s)} data-testid="drawer-edit-button">Edit</Button>
              {s.status === "TENTATIVE" && (
                <Button className="h-9 px-4 text-[11px]" onClick={confirm} disabled={s.needs_attention} title={s.needs_attention ? "Resolve or acknowledge all flags first" : undefined} data-testid="drawer-confirm-button">Confirm</Button>
              )}
              <Button variant="ghost" className="h-9 px-4 text-[11px] hover:text-signal" onClick={() => setCancelling(true)} data-testid="drawer-cancel-shift-button">Cancel shift</Button>
              {s.status === "TENTATIVE" && s.needs_attention && <p data-testid="drawer-confirm-blocked-note" className="w-full text-xs text-red-300">Can't confirm while flags are unresolved.</p>}
            </div>
          )}
          {s.attention_flags.length > 0 && (
            <section>
              <h3 className="label-caps mb-3 text-zinc-400">Attention flags</h3>
              <ul className="space-y-2">{[...s.attention_flags].reverse().map((f) => <FlagItem key={f.id} shiftId={s.id} flag={f} onDone={changed} />)}</ul>
            </section>
          )}
          {s.status !== "CANCELLED" && <ResponsePanel s={s} onChanged={changed} />}
          <SeriesPanel s={s} onChanged={changed} />
          <dl>
            <Row label="Employee" testId="drawer-employee"><Link to={`/admin/employees/${s.employee_id}`} className="hover:text-signal">{s.employee_name}</Link></Row>
            <Row label="Venue" testId="drawer-venue"><Link to={`/admin/venues/${s.venue_id}`} className="hover:text-signal">{s.venue_name}</Link>{s.venue_status === "ARCHIVED" && <span className="ml-2 text-xs text-zinc-500">(archived)</span>}</Row>
            <Row label="Address" testId="drawer-address">{s.venue_address ?? "—"}</Row>
            <Row label="Role" testId="drawer-role">{s.role ?? "—"}</Row>
            <Row label="Instructions" testId="drawer-instructions"><span className="whitespace-pre-wrap">{s.instructions ?? "—"}</span></Row>
            {s.start_at_utc && <Row label="UTC" testId="drawer-utc"><span className="font-mono text-xs text-zinc-400">{s.start_at_utc} → {s.end_at_utc}</span></Row>}
            {s.status === "CANCELLED" && <Row label="Cancel reason" testId="drawer-cancel-reason">{s.cancel_reason} · {fmtDate(s.cancelled_at)}</Row>}
          </dl>
          <section>
            <h3 className="label-caps mb-4 text-zinc-400">Assignment history</h3>
            <ShiftHistory items={history} />
          </section>
        </div>
      )}
      {s && <CancelModal shift={s} open={cancelling} onClose={() => setCancelling(false)} onDone={() => { setCancelling(false); changed(); }} />}
    </Drawer>
  );
}

function CancelModal({ shift, open, onClose, onDone }: { shift: ShiftAdmin; open: boolean; onClose: () => void; onDone: () => void }) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const submit = async () => {
    setBusy(true);
    setError(null);
    try {
      await shiftApi.cancel(shift.id, reason.trim());
      toast.success("Shift cancelled");
      setReason("");
      onDone();
    } catch (err) {
      setError(errorMessage(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Modal open={open} onClose={onClose} title="Cancel shift?" testId="shift-cancel-modal">
      <p className="mb-5 text-sm text-zinc-300">{shift.employee_name} at {shift.venue_name} on {fmtWorkDate(shift.work_date)}. Cancelled shifts stay visible but can't be edited or restored.</p>
      <Field label="Reason (required)" htmlFor="shift-cancel-reason">
        <TextInput id="shift-cancel-reason" value={reason} onChange={(e) => setReason(e.target.value)} data-testid="shift-cancel-reason-input" />
      </Field>
      <div className="mt-4"><ErrorText message={error} testId="shift-cancel-error" /></div>
      <div className="mt-6 flex justify-end gap-3">
        <Button variant="secondary" onClick={onClose} data-testid="shift-cancel-back-button">Keep shift</Button>
        <Button loading={busy} disabled={reason.trim().length < 3} onClick={submit} data-testid="shift-cancel-confirm-button">Cancel shift</Button>
      </div>
    </Modal>
  );
}
